<template>
	<v-card class="resource-page" style="border-radius: 10px; max-width: 600px" flat>
		<PageBlocked v-if="isBlocked" :blockRole="blockRole" />
		<div v-else-if="isBlocked !== null">
			<v-card-text>
				<div
					class="resources-page__title-wrapper align-center"
					v-bind:style="{ justifyContent: folderTitle ? 'space-between' : 'flex-end' }"
				>
					<h3
						v-if="folderTitle"
						class="resource-page__page-title"
						:style="{ marginBottom: isAdvancedResources ? '12px' : '0' }"
					>
						{{ folderTitle }}
					</h3>
					<v-btn
						v-if="isAdvancedResources"
						class="resources-page__list-btn mr-3"
						:ripple="false"
						icon
						@click="toggleList"
					>
						<Icon v-if="listView" type="grid2" color="var(--accent-color)" :size="34" />
						<Icon v-else type="list" color="var(--accent-color)" :size="34" />
					</v-btn>
				</div>
				<filter-chip-button
					:isButtonActive="isButtonActive"
					:getAndCallDynamicFunction="getAndCallDynamicFunction"
					:normalizedFeedFilterChips="normalizedResourcesFilterChips"
					:clearFilter="clearFilter"
				/>
				<PostFilterModal
					@closeModal="closeModal"
					:show="showModal"
					entityType="resources"
					:error="error"
					:show-error="showError"
					:close-alert="closeAlert"
					:selected-button="selectedButton"
					:current-active-button="this.currentActiveButton"
					:chipsLists="this.lists"
					:currentActiveButtonTitle="currentActiveButtonTitle"
					@applySelection="applySelection"
					@setItems="setSelectedItems"
				/>

				<v-skeleton-loader
					style="max-width: 600px"
					v-bind="attrs"
					type="card"
					v-if="this.isLoading"
				>
				</v-skeleton-loader>

				<AdvancedResourceList
					v-if="
						!this.isLoading &&
						this.resourcesItems.length > 0 &&
						isAdvancedResources &&
						!listView
					"
					:resourcesItems="resourcesItems"
				/>

				<ResourceList
					v-if="
						!this.isLoading &&
						this.resourcesItems.length > 0 &&
						(!isAdvancedResources || listView)
					"
					:resourcesItems="resourcesItems"
				/>

				<div
					v-if="!this.isLoading && this.resourcesItems.length === 0"
					class="mt-5 align-center"
				>
					<NewsFeedEmpty
						title="Resources is empty at the moment."
						description="Check back later."
					/>
				</div>

				<div
					class="d-flex my-9 ma-auto justify-center home-page__pagination"
					v-if="!this.isLoading && this.resourcesItems.length > 0"
				>
					<Pagination
						:page="filters.page"
						:pageCount="pageCount"
						@pageChange="onPageChange"
					/>
				</div>
			</v-card-text>
		</div>
	</v-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import searchIcon from '@/assets/images/search.svg'
import NewsFeedEmpty from '@/components/Post/NewsFeedEmpty.vue'
import ResourceList from '@/components/Resource/ResourceList.vue'
import AdvancedResourceList from '../components/Resource/AdvancedResourceList.vue'
import Block from '@/mixins/Block.js'
import PageBlocked from '@/components/PageBlocked'
import Pagination from '@/components/Pagination.vue'
import _ from 'lodash'
import PostFilterModal from '@/components/Modals/PostFilterModal.vue'
import FilterChipButton from '@/components/FilterChipButtons.vue'

export default {
	name: 'ResourcePage',
	mixins: [Block],
	components: {
		FilterChipButton,
		PostFilterModal,
		ResourceList,
		NewsFeedEmpty,
		AdvancedResourceList,
		PageBlocked,
		Pagination,
	},
	beforeMount() {
		this.clearButtonHandler()
	},
	computed: {
		...mapGetters('resources', [
			'resourcesList',
			'isLoading',
			'singleMeta',
			'breadCrumbs',
			'listView',
		]),
		...mapGetters('list', ['listsMeta', 'lists']),
		chapters() {
			if (this.profile && this.profile.chapters.length > 0) {
				return this.profile.chapters.filter((item) => !item.hidden_subgroup)
			}
			return []
		},
		normalizedResourcesFilterChips() {
			if (!this.appCore) {
				return []
			}
			const currentRouteName = 'resources'

			const currentRouteQuery = {}
			if ('advance' in this.$route.query) {
				currentRouteQuery.advance = this.$route.query.advance
			}

			const matchingMenuItems = this.appCore.appMenuNormalized.baseMenuItems.filter(
				(item) => {
					return item.route.name === currentRouteName
				}
			)

			if (matchingMenuItems.length === 0) {
				return []
			}

			const matchingMenuItem =
				matchingMenuItems.find((item) => {
					const itemQuery = item.route.query || {}
					return _.isEqual(itemQuery, currentRouteQuery)
				}) || matchingMenuItems[0]

			const { chips = [] } = matchingMenuItem
			const isChipsEmpty =
				chips.length === 0 || (chips.length === 1 && chips[0].name === 'clear')
			this.isShowChips = !isChipsEmpty
			return chips
		},

		isVerticalTabs() {
			return this.$vuetify.breakpoint.mdAndUp
		},
		normalizedBreadCrumbs() {
			return this.breadCrumbs || []
		},
		resourcesItems() {
			return this.resourcesList || []
		},
		pageCount() {
			if (!this.singleMeta) {
				return 0
			}

			return +this.singleMeta.pageCount
		},
		isAdvancedResources() {
			return !!this.$route.query.advance
		},
		folderTitle() {
			return this.$route.query.title || null
		},
	},
	watch: {
		$route: 'fetchData',
		'filters.tags': function (next, prev) {
			const isValuesEmptyOrEqual =
				(next.length <= 0 && prev.length <= 0) || _.isEqual(next, prev)
			if (isValuesEmptyOrEqual) {
				return false
			}

			const params = {
				chapter_id: this.selectedChips.selectedChapters,
				filter: this.filters.filter,
				search: this.filters.search,
				page: 1,
				...this.filters.chips,
			}

			this.filters.page = 1
			const queryString = this.formatFilters(params)

			this.getResourcesById(queryString)
		},

		'filters.filter': function (val) {
			const params = {
				filter: val,
				chapter_id: this.filters.groups,
				search: this.filters.search,
				user_id: this.filters.userId,
				page: 1,
			}

			this.filters.page = 1
			const queryString = this.formatFilters(params)

			this.getResourcesById(queryString)
		},

		selectedButton(cur) {
			if (cur.length === 0) {
				this.selectedButton = ['clear']
			}
		},
		'filters.page': {
			handler(newValue, oldValue) {
				if (newValue !== oldValue) {
					const params = {
						search: this.filters.search,
						page: newValue,
						parentId: +this.$route.params.id,
					}
					const queryString = this.formatFilters(params)
					this.getResourcesById(queryString)
				}
			},
			'singleMeta.pageCount': {
				handler(newValue) {
					if (this.filters.page > newValue) {
						this.filters.page = 1
					}
				},
			},
		},
		'singleMeta.pageCount': {
			handler(newValue) {
				if (this.filters.page > newValue) {
					this.filters.page = 1
				}
			},
		},
	},
	methods: {
		...mapActions('resources', [
			'getResourcesById',
			'breadCrumbHandler',
			'clearResources',
			'toggleListView',
			'getCategories',
			'getResourcesById',
		]),
		...mapActions('tag', ['setDefaultStructure']),

		filterSpecifiedValues(list, filterValues) {
			return list.filter((item) => !filterValues.includes(item))
		},
		getFilteredChips(selectedButton, handleButtonName, mutuallyExclusiveButtons) {
			const isButtonPresentInlist = this.selectedButton.includes(handleButtonName)

			if (!isButtonPresentInlist) {
				return this.filterSpecifiedValues(
					[...this.selectedButton, handleButtonName],
					mutuallyExclusiveButtons
				)
			} else {
				return this.selectedButton
			}
		},
		setSelectedItems(item) {
			const { listItem, selectedType, idColumnName = 'id' } = item
			this.setDynamicItems(listItem, selectedType, idColumnName)
		},
		setDynamicItems(item, selectedType, chapterId) {
			if (
				this.selectedChips[selectedType] &&
				this.selectedChips[selectedType].includes(item[chapterId])
			) {
				this.selectedChips[selectedType] = this.selectedChips[selectedType].filter(
					(itemFilter) => itemFilter !== item[chapterId]
				)
			} else {
				this.selectedChips[selectedType] = [
					...(this.selectedChips[selectedType] || []),
					item[chapterId],
				]
			}
		},
		applySelection() {
			const prevChips = { ...this.filters.chips }

			Object.keys(this.selectedChips).forEach((chip) => {
				if (!this.filters.chips[chip]) {
					this.filters.chips[chip] = []
				}
				this.filters.chips[chip] = [...new Set([...this.selectedChips[chip]])]
			})

			const updatedChips = { ...this.filters.chips }

			const isValuesEqual = _.isEqual(prevChips, updatedChips)

			if (isValuesEqual) {
				return false
			}
			const params = {
				chapter_id: this.selectedChips.selectedChapters,
				filter: this.filters.filter,
				search: this.filters.search,
				page: 1,
				parentId: +this.$route.params.id,
				...this.filters.chips,
			}

			this.filters.page = 1
			const queryString = this.formatFilters(params)
			this.getResourcesById(queryString)

			this.showModal = false
		},
		closeAlert() {
			this.showError = false
			this.error = ''
		},
		chaptersButtonHandler() {
			this.currentActiveButton = 'chapters'
			this.currentActiveButtonTitle = this.normalizedResourcesFilterChips.find(
				(item) => item.name === this.currentActiveButton
			).content

			const mutuallyExclusiveButtons = ['clear']

			this.selectedButton = this.getFilteredChips(
				this.selectedButton,
				this.currentActiveButton,
				mutuallyExclusiveButtons
			)

			const isHandleButtonActive = this.selectedButton.includes(this.currentActiveButton)

			if (!isHandleButtonActive) {
				this.selectedChapters = []
				this.filters = {
					...this.filters,
					groups: [],
				}
			}

			this.showModal = isHandleButtonActive
		},
		clearButtonHandler() {
			this.currentActiveButton = 'clear'
			this.selectedButton = ['clear']
			this.selectedChapters = []
			this.filters.groups = []
			this.setDefaultStructure()

			if (Array.isArray(this.profile.chapters)) {
				this.profile.chapters = this.profile.chapters.map((el) => ({
					...el,
					checked: false,
				}))
			}

			this.selectedChips = {}

			this.filters = {
				filter: null,
				search: this.filters.search,
				chips: {},
				groups: [],
				tags: [],
				userId: null,
				page: 1,
			}

			this.filters.page = 1

			const params = {
				filter: null,
				groups: [],
				tags: [],
				userId: null,
				search: '',
				page: 1,
			}
			const queryString = this.formatFilters(params)

			this.getResourcesById(queryString)

			this.showModal = false
		},
		closeModal() {
			this.showModal = false
			const mutuallyExclusiveButtons = ['clear']

			if (
				this.currentActiveButton !== 'clear' &&
				this.defaultFilters.every((filter) => filter !== this.currentActiveButton)
			) {
				this.selectedChips[this.currentActiveButton] = []
				this.filters.chips[this.currentActiveButton] = []
			}

			this.selectedButton = this.getFilteredChips(
				this.selectedButton,
				this.currentActiveButton,
				mutuallyExclusiveButtons
			)
		},
		clearFilter(name) {
			switch (name) {
				case 'chapters':
					this.selectedChapters = []
					this.filters.groups = []

					if (Array.isArray(this.profile.chapters)) {
						this.profile.chapters = this.profile.chapters.map((el) => ({
							...el,
							checked: false,
						}))
					}
					break
				case 'tags':
					this.setDefaultStructure()
					break
				case 'search':
					this.filters.search = ''
					break
				case 'myposts':
					this.filters.userId = null
					break
				case 'admin':
					this.filters.filter = null
					break
				default:
			}

			this.selectedButton = this.selectedButton.filter((button) => button !== name)

			this.showModal = false

			const params = {
				filter: this.filters.filter,
				chapter_id: this.selectedChapters,
				user_id: this.filters.userId,
				search: this.filters.search,
				page: 1,
			}
			const queryString = this.formatFilters(params)
			this.getResourcesById(queryString)
		},
		isButtonActive(name) {
			return this.selectedButton.includes(name)
		},
		getAndCallDynamicFunction(name) {
			if (name !== 'clear' && this.defaultFilters.every((filter) => filter !== name)) {
				const chip = this.listsMeta.find((item) => item.source === name)
				if (chip) {
					this.currentActiveButton = name
					this.currentActiveButtonTitle = this.normalizedResourcesFilterChips.find(
						(item) => item.name === this.currentActiveButton
					).content

					const mutuallyExclusiveButtons = ['clear']

					this.selectedButton = this.getFilteredChips(
						this.selectedButton,
						this.currentActiveButton,
						mutuallyExclusiveButtons
					)
					const isHandleButtonActive = this.selectedButton.includes(
						this.currentActiveButton
					)
					if (!isHandleButtonActive) {
						this.selectedChips[name] = []
						this.filters.chips = {
							...this.filters.chips,
							[name]: [],
						}

						this.currentActiveButton = 'clear'

						const params = {
							filter: this.filters.filter,
							search: this.filters.search,
							page: 1,
							...this.filters.chips,
						}

						this.filters.page = 1
						const queryString = this.formatFilters(params)
						this.getResourcesById(queryString)
					}

					this.showModal = isHandleButtonActive
				} else {
					console.error('function not found')
				}
			} else {
				let stringWithUnderscores = name.replace(/-/g, '_')
				const dynamicFunctionName = `${stringWithUnderscores}ButtonHandler`
				if (this.selectedButton.includes(stringWithUnderscores) && name !== 'clear') {
					this.showModal = true
				}
				const dynamicFunction = this[dynamicFunctionName]
				if (typeof dynamicFunction === 'function') {
					dynamicFunction()
				} else {
					console.error('function not found')
				}
			}
		},

		isLastElement(index) {
			return index === this.normalizedBreadCrumbs.length - 1
		},
		fetchData() {
			const params = {
				page: 1,
				parentId: +this.$route.params.id,
			}
			this.filters.page = 1
			const queryString = this.formatFilters(params)

			this.getResourcesById(queryString)
		},
		onPageChange(page) {
			const params = {
				search: this.filters.search,
				page: page,
				parentId: +this.$route.params.id,
			}
			this.filters.page = page
			const queryString = this.formatFilters(params)
			this.getResourcesById(queryString)
		},
		formatFilters(filters) {
			const { filter = null, chapter_id = [], search, page, tag_ids = [] } = filters

			let groupIds = chapter_id
				.filter((id) => !!id)
				.map((id) => `chapter_id[]=${id}`)
				.join('&')

			let tagIds = tag_ids
				.filter((id) => !!id)
				.map((id) => `tag_ids[]=${id}`)
				.join('&')

			let queryStringParts = []

			let queryString = `parentId=${+this.$route.params.id}&page=${page}`

			if (groupIds) queryStringParts.push(groupIds)
			if (tagIds) queryStringParts.push(tagIds)

			if (queryStringParts.length > 0) {
				queryString += `&${queryStringParts.join('&')}`
			}

			if (filter) {
				queryString += `${queryString ? '&' : ''}filter=${filter}`
			}
			if (search !== undefined && search.length > 0) {
				queryString += `${queryString ? '&' : ''}search=${encodeURIComponent(search)}`
			}

			return queryString
		},

		filterSearch(e) {
			clearTimeout(this.timer)

			const params = {
				search: e.target.value,
				page: 1,
			}
			this.filters.page = 1
			const queryString = this.formatFilters(params)

			this.timer = setTimeout(() => {
				this.getResourcesById(queryString)
			}, 500)
		},
		toggleTag(e) {
			this.currentTag = e
		},
		toggleList() {
			this.toggleListView()
		},
	},
	data() {
		return {
			selectedChips: {},
			selectedChapters: [],
			showError: false,
			error: '',
			showModal: false,
			selectedButton: ['clear'],
			currentActiveButton: '',
			currentActiveButtonTitle: '',
			isHovered: false,
			filters: {
				filter: null,
				chips: {},
				page: 1,
				groups: [],
				parentId: null,
				search: '',
				tags: [],
			},
			searchIcon,
			attrs: {
				class: 'mb-6 mt-6',
				elevation: 0,
			},
			currentTag: 'All',
			defaultFilters: ['chapters', 'tags', 'tracks', 'category', 'date'],
			tags: ['All', 'Admin', 'My Groups', 'Tags'],
			pageBlockedName: 'resources',
		}
	},
	mounted() {
		const params = {
			page: 1,
			parentId: +this.$route.params.id,
		}
		const queryString = this.formatFilters(params)
		if (!this.isBlocked || this.isBlocked === null) {
			if (this.singleMeta) {
				this.filters.page = +this.singleMeta.currentPage
				if (this.singleMeta.currentPage === '1') {
					this.getResourcesById(queryString)
				}
			} else {
				this.getResourcesById(queryString)
			}
		}
	},
	beforeDestroy() {
		this.clearResources()
	},
	isAdvancedResources() {
		return !!this.$route.query.advance
	},
}
</script>

<style lang="scss">
.resource-page {
	max-width: 600px;
	width: 100%;
	border-radius: 10px !important;
	margin-bottom: 30px;
	background-color: transparent !important;

	@media (max-width: 480px) {
		margin-bottom: 12px;
	}

	&__page-title {
		font-size: 24px;
		font-weight: 700;
		font-family: Montserrat, sans-serif;
		color: #4a4a4a;
		line-height: 29px;
		margin: 0 !important;
		overflow: hidden;
		text-overflow: ellipsis;
		display: -webkit-box;
		-webkit-line-clamp: 1;
		-webkit-box-orient: vertical;
		word-break: break-word;
	}

	&__title {
		font-style: normal;
		cursor: pointer;
		font-weight: bold;
		font-size: 40px;
		line-height: 35px;
		color: #202232;
	}
}

.navigate-back {
	&__icon {
		color: var(--accent-color);
	}

	&__text {
		color: var(--accent-color);
		font-size: 15px;
		font-weight: 700;
		line-height: 18px;
		text-decoration: underline;
	}
}

.cursor-pointer {
	cursor: pointer;
}

@media screen and (max-width: 960px) {
	.resource-page__title {
		font-size: 25px !important;
	}
}
</style>
